import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  ChatCircleDots, X, Sparkle, Question, Book, CurrencyDollar,
  Microphone, SignIn, Envelope, PlayCircle, ArrowRight, Lightning,
} from "@phosphor-icons/react";

// Scripted decision-tree. Keys are node IDs, each node has title, body (JSX), options (next nodes).
const NODES = {
  root: {
    title: "Hey there 👋 I'm Ekko, your study buddy.",
    body: () => (
      <p className="text-sm text-[color:var(--ink-muted)]">
        What can I help with today? Pick a topic — I&apos;ll have an answer ready.
      </p>
    ),
    options: [
      { label: "How does Ekkol work?", icon: <Book size={14}/>, to: "how" },
      { label: "Mic not working?", icon: <Microphone size={14}/>, to: "mic" },
      { label: "Sign-in trouble", icon: <SignIn size={14}/>, to: "signin" },
      { label: "Pricing & plans", icon: <CurrencyDollar size={14}/>, to: "pricing" },
      { label: "See a demo", icon: <PlayCircle size={14}/>, to: "demo" },
      { label: "Talk to a human", icon: <Envelope size={14}/>, to: "human" },
    ],
  },
  how: {
    title: "Here's the magic ✨",
    body: () => (
      <div className="space-y-2 text-sm">
        <p><strong>Ekkol reads.</strong> You hear your study material aloud.</p>
        <p><strong>You speak.</strong> Repeat back what you remember.</p>
        <p><strong>Ekkol scores.</strong> Concept-match AI checks meaning, not just words.</p>
        <p className="pt-2 text-[color:var(--ink-muted)]">
          Walk, commute, wash dishes — hands-free mode handles everything with just your voice.
        </p>
      </div>
    ),
    options: [
      { label: "Start a free deck →", icon: <Sparkle size={14}/>, link: "/login" },
      { label: "How do voice commands work?", to: "voice" },
      { label: "Back", to: "root" },
    ],
  },
  voice: {
    title: "Voice commands — no hands needed",
    body: () => (
      <div className="text-sm space-y-2">
        <p>In hands-free mode, just say:</p>
        <ul className="list-disc pl-5 space-y-1 text-[color:var(--ink-muted)]">
          <li>&ldquo;<strong>next</strong>&rdquo; · advance card</li>
          <li>&ldquo;<strong>previous</strong>&rdquo; · go back</li>
          <li>&ldquo;<strong>repeat</strong>&rdquo; · re-read</li>
          <li>&ldquo;<strong>hint</strong>&rdquo; · get a clue</li>
          <li>&ldquo;<strong>pause</strong>&rdquo; / &ldquo;<strong>stop</strong>&rdquo;</li>
        </ul>
      </div>
    ),
    options: [{ label: "Back", to: "how" }],
  },
  mic: {
    title: "Let's fix that mic 🎤",
    body: () => (
      <div className="text-sm space-y-2">
        <p>Most common culprits:</p>
        <ol className="list-decimal pl-5 space-y-1">
          <li>Browser <strong>blocked mic permission</strong> — click the 🔒 icon in your address bar and allow the mic</li>
          <li>You&apos;re on <strong>Safari or Firefox</strong> — try Chrome or Edge for best speech recognition</li>
          <li>Headset plugged in after page loaded — refresh the page</li>
          <li>Beep too soft? <strong>Profile → Beep volume</strong> — crank it up</li>
        </ol>
      </div>
    ),
    options: [
      { label: "Still broken — contact support", to: "human" },
      { label: "Back", to: "root" },
    ],
  },
  signin: {
    title: "Sign-in help",
    body: () => (
      <div className="text-sm space-y-2">
        <p>Ekkol uses <strong>Google sign-in</strong> — no passwords to remember.</p>
        <p className="text-[color:var(--ink-muted)]">
          If the popup gets blocked, check your browser&apos;s popup settings for ekkol.app, then try again.
        </p>
      </div>
    ),
    options: [
      { label: "Try signing in →", link: "/login" },
      { label: "Contact support", to: "human" },
      { label: "Back", to: "root" },
    ],
  },
  pricing: {
    title: "Plans & pricing",
    body: () => (
      <div className="text-sm space-y-2">
        <p><strong>Free</strong> · 5 decks, generous limits, forever free</p>
        <p><strong>Plus</strong> · $7.99/mo or $59/yr — unlimited decks, Smart Review, premium voices</p>
        <p><strong>Edu</strong> · $4.99/student/mo (min 5) — perfect for classrooms</p>
        <p><strong>Enterprise</strong> · SSO + SCORM + custom — contact sales</p>
      </div>
    ),
    options: [
      { label: "See full pricing →", link: "/pricing" },
      { label: "Student discount?", to: "student" },
      { label: "Back", to: "root" },
    ],
  },
  student: {
    title: "Yes — 50% off for students 🎓",
    body: () => (
      <p className="text-sm">
        Email <strong>hello@ekkol.app</strong> with your <em>.edu</em> address and we&apos;ll send
        you a promo code worth 50% off Plus for a year.
      </p>
    ),
    options: [{ label: "Back", to: "pricing" }],
  },
  demo: {
    title: "See Ekkol in action",
    body: () => (
      <div className="text-sm space-y-2">
        <p>The best demo is <strong>doing it</strong> — signup auto-loads a sample &ldquo;Getting Started&rdquo; deck.</p>
        <p className="text-[color:var(--ink-muted)]">No credit card, no email confirmation, no fluff.</p>
      </div>
    ),
    options: [
      { label: "Start free — try it now →", icon: <Sparkle size={14}/>, link: "/login" },
      { label: "Back", to: "root" },
    ],
  },
  human: {
    title: "We'd love to hear from you 💌",
    body: () => (
      <div className="text-sm space-y-2">
        <p>Email us — we read everything:</p>
        <ul className="text-[color:var(--ink-muted)] pl-5 list-disc space-y-1">
          <li><strong>support@ekkol.app</strong> — bugs, how-to</li>
          <li><strong>sales@ekkol.app</strong> — schools, teams, enterprise</li>
          <li><strong>hello@ekkol.app</strong> — partnerships, press, feedback</li>
        </ul>
        <p className="pt-2 text-xs text-[color:var(--ink-muted)]">We typically reply within 24 hours.</p>
      </div>
    ),
    options: [
      { label: "Open email app", link: "mailto:support@ekkol.app" },
      { label: "Browse FAQ", link: "/help" },
      { label: "Back", to: "root" },
    ],
  },
};

export default function HelpBubble() {
  const [open, setOpen] = useState(false);
  const [node, setNode] = useState("root");
  const [history, setHistory] = useState([]);
  const panelRef = useRef(null);

  useEffect(() => {
    if (open && panelRef.current) {
      panelRef.current.scrollTop = 0;
    }
  }, [open, node]);

  const current = NODES[node] || NODES.root;

  const handleNav = (opt) => {
    if (opt.link) {
      if (opt.link.startsWith("http") || opt.link.startsWith("mailto:")) {
        window.open(opt.link, "_blank", "noopener,noreferrer");
      } else {
        window.location.href = opt.link;
      }
      return;
    }
    if (opt.to) {
      if (opt.to === "root") {
        setHistory([]);
      } else {
        setHistory((h) => [...h, node]);
      }
      setNode(opt.to);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-20 right-5 z-[60] bg-[color:var(--ink)] text-white rounded-full shadow-lg hover:scale-105 transition-all p-4 flex items-center gap-2 group"
        aria-label="Open help"
        data-testid="help-bubble-toggle"
        style={{ boxShadow: "0 8px 24px rgba(10, 25, 47, 0.25)" }}
      >
        {open ? <X size={20} weight="bold"/> : <ChatCircleDots size={22} weight="duotone"/>}
        {!open && (
          <span className="font-mono text-xs uppercase tracking-widest hidden group-hover:inline pr-1">
            Need help?
          </span>
        )}
      </button>

      {open && (
        <div
          ref={panelRef}
          className="fixed bottom-40 right-5 z-[60] w-[92vw] max-w-[380px] max-h-[70vh] bg-white border rule shadow-2xl flex flex-col fade-in overflow-hidden"
          data-testid="help-bubble-panel"
          style={{ boxShadow: "0 20px 60px rgba(10, 25, 47, 0.25)" }}
        >
          {/* Header */}
          <div className="bg-[color:var(--ink)] text-white px-5 py-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[color:var(--vermilion)] flex items-center justify-center">
              <Lightning size={16} weight="fill"/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold">Ekko · Help Assistant</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-white/60">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-[color:var(--success)] mr-1.5 align-middle"/>
                Online · instant replies
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
            <div className="bg-[color:var(--surface-alt)] border rule p-4">
              <div className="font-serif text-lg leading-snug mb-2">{current.title}</div>
              <div>{current.body()}</div>
            </div>

            <div className="space-y-2">
              {current.options.map((opt, i) => (
                <button
                  key={i}
                  onClick={() => handleNav(opt)}
                  className="w-full text-left bg-white hover:bg-[color:var(--surface-alt)] border rule px-4 py-3 text-sm flex items-center justify-between gap-3 transition-colors group"
                  data-testid={`help-option-${opt.label.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`}
                >
                  <span className="flex items-center gap-2">
                    {opt.icon && <span className="text-[color:var(--ink-muted)]">{opt.icon}</span>}
                    {opt.label}
                  </span>
                  <ArrowRight size={12} className="text-[color:var(--ink-faint)] group-hover:text-[color:var(--ink)] transition-colors"/>
                </button>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="border-t rule px-5 py-3 bg-[color:var(--surface-alt)] flex items-center justify-between text-xs text-[color:var(--ink-muted)]">
            <Link
              to="/help"
              onClick={() => setOpen(false)}
              className="link-underline inline-flex items-center gap-1"
              data-testid="help-bubble-browse-faq"
            >
              <Question size={12}/> Browse full FAQ
            </Link>
            <span className="font-mono text-[10px] tracking-widest">EKKOL · [ EH·KOL ]</span>
          </div>
        </div>
      )}
    </>
  );
}
